import React, { useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';

const countryCodes = [
  { code: '+1', flag: '\u{1F1FA}\u{1F1F8}' }, { code: '+44', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: '+91', flag: '\u{1F1EE}\u{1F1F3}' }, { code: '+86', flag: '\u{1F1E8}\u{1F1F3}' },
  { code: '+49', flag: '\u{1F1E9}\u{1F1EA}' }, { code: '+33', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: '+61', flag: '\u{1F1E6}\u{1F1FA}' }, { code: '+81', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: '+82', flag: '\u{1F1F0}\u{1F1F7}' }, { code: '+971', flag: '\u{1F1E6}\u{1F1EA}' },
  { code: '+55', flag: '\u{1F1E7}\u{1F1F7}' }, { code: '+52', flag: '\u{1F1F2}\u{1F1FD}' },
  { code: '+65', flag: '\u{1F1F8}\u{1F1EC}' }, { code: '+64', flag: '\u{1F1F3}\u{1F1FF}' },
];

const HeroBanner = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
  const [errors, setErrors] = useState({ email: '', phone: '' });

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const handlePhoneChange = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 10) {
      setFormData(prev => ({ ...prev, phone: digits }));
      setErrors(prev => ({ ...prev, phone: digits && digits.length !== 10 ? 'Phone must be 10 digits' : '' }));
    }
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    let hasErrors = false;
    const newErrors = { email: '', phone: '' };
    if (!validateEmail(formData.email)) { newErrors.email = 'Enter a valid email'; hasErrors = true; }
    if (formData.phone.length !== 10) { newErrors.phone = 'Phone must be 10 digits'; hasErrors = true; }
    if (hasErrors) { setErrors(newErrors); return; }
    toast({ title: "Request Received", description: "Our trademark team will reach out within 24 hours." });
    setFormData({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
    setErrors({ email: '', phone: '' });
  };

  return (
    <section id="home" className="relative pt-28 pb-24 bg-white" data-testid="hero-section">
      <div className="container mx-auto px-6 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          {/* Left */}
          <div className="lg:col-span-7 pt-8">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-5" data-testid="hero-badge">
              US Trademark Registration Services
            </p>
            <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-[#09090B] leading-none mb-6" style={{fontFamily:'Outfit,sans-serif'}} data-testid="hero-heading">
              Your Brand Deserves<br/>
              <span className="text-[#002FA7]">Legal Protection.</span>
            </h1>
            <p className="text-base leading-relaxed text-zinc-600 max-w-lg mb-8" data-testid="hero-description">
              Federal trademark registration, expert USPTO filing, and comprehensive brand protection. Secure your name, logo, or slogan with attorneys who handle thousands of applications every year. Plans start at $45.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {['Federal trademark protection', 'Expert USPTO filing', 'Amazon Brand Registry'].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-zinc-500">
                  <Check className="w-4 h-4 text-[#002FA7]" />{t}
                </div>
              ))}
            </div>
          </div>

          {/* Right Form */}
          <div className="lg:col-span-5">
            <div className="bg-white border border-zinc-200 rounded-lg p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)]" data-testid="hero-form-card">
              <h3 className="text-xl font-bold text-[#09090B] mb-1" style={{fontFamily:'Outfit,sans-serif'}}>Start Your Registration</h3>
              <p className="text-sm text-zinc-500 mb-6">No hidden fees. Plans from $45.</p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="hero-form">
                <div><Label className="text-zinc-700 text-sm font-medium">Name *</Label>
                  <Input placeholder="Full name" value={formData.name} onChange={(e) => setFormData(p => ({...p, name: e.target.value}))} required className="mt-1.5 h-10 border-zinc-200 focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20" data-testid="hero-name-input" /></div>

                <div><Label className="text-zinc-700 text-sm font-medium">Email *</Label>
                  <Input type="email" placeholder="you@company.com" value={formData.email} onChange={(e) => { setFormData(p => ({...p, email: e.target.value})); setErrors(p => ({...p, email: ''})); }} required className={`mt-1.5 h-10 ${errors.email ? 'border-red-400' : 'border-zinc-200'} focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20`} data-testid="hero-email-input" />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}</div>

                <div><Label className="text-zinc-700 text-sm font-medium">Phone *</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Select value={formData.countryCode} onValueChange={(v) => setFormData(p => ({...p, countryCode: v}))}>
                      <SelectTrigger className="w-[90px] h-10 border-zinc-200 focus:border-[#002FA7]" data-testid="hero-country-code"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-[250px]">{countryCodes.map((item) => (<SelectItem key={item.code} value={item.code}><span className="flex items-center gap-1"><span>{item.flag}</span><span className="text-xs">{item.code}</span></span></SelectItem>))}</SelectContent>
                    </Select>
                    <Input type="tel" placeholder="1234567890" value={formData.phone} onChange={(e) => handlePhoneChange(e.target.value)} required maxLength={10} className={`flex-1 h-10 ${errors.phone ? 'border-red-400' : 'border-zinc-200'} focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20`} data-testid="hero-phone-input" />
                  </div>
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}</div>

                <div><Label className="text-zinc-700 text-sm font-medium">What do you need?</Label>
                  <Textarea placeholder="e.g. Register my brand name and logo" rows={2} value={formData.description} onChange={(e) => setFormData(p => ({...p, description: e.target.value}))} className="mt-1.5 border-zinc-200 focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 resize-none" data-testid="hero-description-input" /></div>

                <Button type="submit" className="w-full btn-lift bg-[#002FA7] hover:bg-[#001D66] text-white h-11 font-semibold rounded-md" data-testid="hero-submit-button">
                  Register My Trademark <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <p className="text-[11px] text-zinc-400 text-center">By submitting, you agree to our Terms and Privacy Policy.</p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
